<template>
  <div
    :class="[
      'vgp_date_picker',
      customSelector ? 'date_range_modification' : '',
      isRange ? 'range_picker' : '',
      hasRangeDiff ? 'range_has_diff' : ''
    ]"
  >
    <vc-date-picker
      ref="calendar"
      v-model="modelValue"
      mode="date"
      :is-range="isRange"
      :is-required="true"
      :attributes="calendarAttributes"
      :locale="calendarLocale"
      :min-date="minDate"
      :max-date="maxDate"
      :columns="columns"
      :popover="{
        placement: 'top-end',
      }"
      @input="onInput"
      @popoverDidShow="onShow"
      @popoverDidHide="onHide"
    >
      <template
        v-if="hasDefaultSlot"
        #default="{ inputValue, togglePopover }"
      >
        <slot
          v-if="$scopedSlots.default"
          :inputValue="inputValue"
          :togglePopover="togglePopover"
        />
        <div
          v-else
          :class="'vgp_date_picker_input'"
        >
          <svg-sprite
            :class="'vgp_date_picker_icon'"
            :name="'calendar'"
            :type="'regular'"
            :size="24"
            @click="() => togglePopover()"
          />
        </div>
      </template>

      <template
        v-if="hasFooterSlot"
        #footer
      >
        <div :class="'vgp_date_picker_footer'">
          <slot
            v-if="$scopedSlots.footer"
            name="footer"
            :setValue="setValue"
          />
          <button
            v-else
            :class="'vgp_date_picker_footer_button'"
            @click.stop="() => setValue(new Date())"
          >
            {{ locales('datepiker_today') }}
          </button>
        </div>
      </template>
    </vc-date-picker>
  </div>
</template>

<script>
export default {
  name: 'VgpDatePicker',
  props: {
    value: { type: Date, default: null },
    workDays: { type: Array, required: false, default: () => [1, 2, 3, 4, 5] },
    rangeValue: {
      type: Object,
      required: false,
      default: () => ({
        start: null,
        end: null,
      }),
    },
    minDate: { type: Object, required: false, default: undefined },
    maxDate: { type: Object, required: false, default: undefined },
    isRange: { type: Boolean, required: false, default: false },
    columns: { type: Number, required: false, default: 1 },
    hasDefaultSlot: { type: Boolean, required: false, default: true },
    hasFooterSlot: { type: Boolean, required: false, default: true },
    customSelector: { type: Boolean, required: false, default: false },
  },
  emits: {
    onChange: String || Array,
    onShow: null,
    onClose: null,
  },
  data() {
    return {
      locales: __,
      calendarLocale: {
        id: this.getCalendarLocale(),
        firstDayOfWeek: +user.settings.start_monday ? 2 : 1,
        masks: {
          input: user.dateFormat ? user.dateFormat : 'YYYY-MM-DD',
        },
      },
      hasRangeDiff: false,
    };
  },
  computed: {
    calendarValue() {
      return this.value ? this.getClosestFutureWorkDay(this.value) : null;
    },

    modelValue() {
      if (this.isRange) {
        return this.rangeValue;
      }

      return this.calendarValue;
    },

    calendarWorkDays() {
      // Day of week begins on Sunday in v-calendar
      return this.workDays.map(day => (day === 7 ? 1 : ++day));
    },

    calendarAttributes() {
      return [
        {
          highlight: {
            contentStyle: { color: '#B2B2B2', borderRadius: '4px' },
            contentClass: 'dayHighlightActive',
          },
          dates: {
            weekdays: [1, 2, 3, 4, 5, 6, 7].filter(day => !this.calendarWorkDays.find(workDay => workDay === day)),
          },
        },
      ];
    },
  },
  watch: {
    modelValue(value) {
      if (this.isRange) {
        const startDate = new Date(value.start);
        const endDate = new Date(value.end);
        const isDifferentDay = startDate.getMonth() !== endDate.getMonth() || startDate.getDate() !== endDate.getDate();

        if (isDifferentDay) {
          this.hasRangeDiff = true;
        } else {
          this.hasRangeDiff = false;
        }
      }
    },
  },
  methods: {
    setValue(value) {
      const closestWorkDay = this.getClosestFutureWorkDay(value);

      this.$refs.calendar.updateValue(closestWorkDay);
      this.$refs.calendar.hidePopover();
    },

    onInput(value) {
      this.$emit('onChange', value);
    },

    onShow() {
      this.$emit('onShow');
    },

    onHide() {
      this.$emit('onClose');
    },

    getClosestFutureWorkDay(date) {
      if (!this.calendarWorkDays.length) {
        return date;
      }

      let closestDay = new Date();

      if (date && date !== '') {
        closestDay = new Date(date);
      }

      let i = 0;

      while (!this.calendarWorkDays.includes(closestDay.getDay() + 1) && i < 7) {
        closestDay.setDate(closestDay.getDate() + 1);
        i++;
      }

      return closestDay;
    },

    move(date) {
      this.$refs.calendar.move(date);
    },

    getCalendarLocale() {
      if (user.locale === 'kr') {
        return 'ko';
      }

      return user.locale;
    },
  },
};
</script>

<style src="./datePicker.less" lang="less"></style>
